/** @jsx jsx */
import { Button, ErrorBoundary, Grid, Spacer, ToolTip } from "@cd2/components"
import { Fields } from "@cd2/forms"
import { P } from "@cd2/text"
import { css, jsx } from "@emotion/core"
import React, { useEffect, useRef, useState } from "react"
import { RemoveRowButton } from "../../Components/Buttons/RemoveRowButton"
import { useWatchState } from "../../hooks/useWatchState"
import { BoxQuantitySwitch } from "../../Pages/OrderRequests/OrderRequestsStandaloneForm"
import { ProductInfoDisplayPanel } from "../../Pages/Stock/Products/ProductInfoDisplayPanel"
import { useSettings } from "../../utils/useSettings"
import { BarscanColumn } from "./BarscanColumn"
import { ProductLocationWithStock, StockDisplay } from "./ItemRowSageProduct"
import {
  BARSCAN_COLUMN_WIDTH,
  DISCOUNT_COLUMN_WIDTH,
  IItem,
  IQuoteItemRegisterOptions,
  IRowProps,
  isDeleted,
  itemTypes,
  LOCATION_COLUMN_WIDTH,
  MARGIN_COLUMN_WIDTH,
  PRICE_COLUMN_WIDTH,
  PRODUCT_SELECT_COLUMN_WIDTH,
  QUANTITY_COLUMN_WIDTH,
} from "./quoteOrderUtils"
import { useReloadKey } from "../../hooks/useReloadKey"
import { ProductSelect2 } from "../../Components/ProductSelect2"

export function ItemRowBuildSetProduct({
  registerItem,
  data,
  onChange,
  quote,
  errors,
  onRemove,
  rawFunctions,
  displayAllocationColumn,
  type,
  isCustomerViewing,
  hideMarginColumn,
  hidePriceColumn,
}: IRowProps) {
  const [productComponentReloadKey, reloadProductSelectComponent] = useReloadKey()
  const [focused, setFocused] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const skeem = window.skeem
  const {
    defaultStockLocation,
    barScanOrders,
    warehouses,
    showDiscountOption,
    dpdProductPrefixes,
    stockWarehouses,
    useExplicitAllocations,
    _loaded,
    useQuoteOrderStandardPricing,
    directDeliveryWarehouse,
    ...settings
  } = useSettings()

  const shouldShowDiscount = !!(showDiscountOption || quote.hasOpportunityCertificate)

  const parentItem = quote.items.find(
    x => !isDeleted(x) && x.type === itemTypes.buildSet && x.build_set_uid == data.build_set_uid,
  ) as IItem | undefined

  function getRegistrationOptions(): IQuoteItemRegisterOptions {
    return {
      onSave(item, _) {
        return item
      },
      validate(item, _) {
        const errors = {} as any
        const warnings: string[] = []

        const parentItem = quote.items.find(
          x =>
            !isDeleted(x) && x.type === itemTypes.buildSet && x.build_set_uid == item.build_set_uid,
        )
        if (!parentItem) {
          errors.product = "Parent item not found"
        }

        if (!item.build_quantity) {
          errors.quantity = "Required"
        }
        if (!item.quantity) {
          errors.quantity = "Required"
        }

        if ((item.quantity ?? 0) < (item.allocated_quantity ?? 0)) {
          errors.allocated_quantity = "Cannot allocate more than quantity"
        }

        return { errors, warnings }
      },
    }
  }

  useEffect(() => {
    if (!_loaded) {
      return
    }
    registerItem(getRegistrationOptions())
  }, [_loaded])

  // Loads product location with free_stock, assigns product location to specified item
  // This is using the raw function to work with the childItems as well as the main item.
  async function handleProductLocationChange(item: IItem, locationId: string) {
    const location = await skeem.fetchOne<Exclude<IItem["product_location"], undefined>>({
      product_locations: {
        attributes: ["name", "free_stock", "warehouse", "specific_location"],
        filter: { matches: { id: locationId } },
      },
    })

    rawFunctions.handleItemChange(item.id, oldItem => ({
      ...oldItem,
      product_location: location,
      stock_location: location.name,
    }))
  }

  function handleChangeQty(qty: number | null) {
    const parentItem = quote.items.find(
      x => !isDeleted(x) && x.type === itemTypes.buildSet && x.build_set_uid == data.build_set_uid,
    ) as IItem
    if (!parentItem) {
      return
    }
    const newQty = (qty ?? 1) * (parentItem.quantity ?? 0)
    onChange({ quantity: newQty, allocated_quantity: newQty, build_quantity: qty })
  }

  // sync quantity with parent item
  useWatchState(
    (_, qty) => {
      if (qty === null) {
        return
      }
      const parentItem = quote.items.find(
        x =>
          !isDeleted(x) && x.type === itemTypes.buildSet && x.build_set_uid == data.build_set_uid,
      ) as IItem
      if (!parentItem) {
        return
      }
      const newQty = (data.build_quantity ?? 1) * (qty ?? 0)
      onChange({ quantity: newQty, allocated_quantity: newQty })
    },
    parentItem?.quantity,
    250,
  )

  async function handleProductChange(productId: string) {
    const newData = await rawFunctions.loadProductDataForSageItemRow(productId)
    onChange({
      ...data,
      ...newData.item,
      id: data.id,
      quantity: data.quantity ?? 0,
      build_quantity: data.build_quantity ?? 1,
      build_set_uid: data.build_set_uid,
      type: itemTypes.buildSetItem,
    })
  }

  const barscanIntegrated = barScanOrders && !quote.warehouseOverride?.disable_barscan

  return (
    <React.Fragment>
      <div
        style={{ outline: "none" }}
        tabIndex={-1}
        onFocus={() => {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
          }
          setFocused(true)
        }}
        onBlur={() => (timeoutRef.current = setTimeout(() => setFocused(false), 400))}
      >
        <Grid
          columns={
            [
              PRODUCT_SELECT_COLUMN_WIDTH,
              barscanIntegrated && !quote.isInternalTransfer && BARSCAN_COLUMN_WIDTH,
              LOCATION_COLUMN_WIDTH,
              QUANTITY_COLUMN_WIDTH,
              useExplicitAllocations && QUANTITY_COLUMN_WIDTH,
              shouldShowDiscount && DISCOUNT_COLUMN_WIDTH,
              !isCustomerViewing && PRICE_COLUMN_WIDTH,
              !hidePriceColumn && !quote.isInternalTransfer && PRICE_COLUMN_WIDTH,
              !hideMarginColumn &&
                !isCustomerViewing &&
                !quote.isInternalTransfer &&
                MARGIN_COLUMN_WIDTH,
            ].filter(Boolean) as any
          }
          gap={6}
        >
          <div>
            <ErrorBoundary
              key={productComponentReloadKey}
              ErrorComponent={() => (
                <div>
                  An error has occurred with this component.{" "}
                  <Button onClick={reloadProductSelectComponent} buttonStyle="text">
                    Click here to refresh the component
                  </Button>{" "}
                </div>
              )}
            >
              <ProductSelect2
                value={data.product}
                onChange={product => handleProductChange(product.id)}
                error={errors.product}
                autoFocus={!data.product}
                preventSelectUnexported
                customerId={quote.customer?.id}
              />
            </ErrorBoundary>

            <BoxQuantitySwitch
              productId={data.product?.id}
              itemQuantity={data.build_quantity}
              onChange={build_quantity => onChange({ build_quantity })}
            />

            {type === "quote" && settings.useQuoteSpecialItems && (
              <Fields.Checkbox
                label="This is a special order"
                value={!!data.special_order_item}
                onChange={value => {
                  onChange({ special_order_item: value })
                }}
              />
            )}
          </div>
          {barscanIntegrated && !quote.isInternalTransfer && (
            <div>
              <BarscanColumn onChange={onChange} values={data} />
            </div>
          )}
          <div>
            <ProductLocationWithStock
              onChange={handleProductLocationChange}
              data={data}
              childItems={[]}
              warehouses={warehouses}
            />
            {focused && !!data.product?.id && !!data.product_location?.id && (
              <React.Fragment>
                <Spacer />
                <StockDisplay
                  productLocationId={data.product_location.id}
                  quoteId={quote.id}
                  quantityAmount={data.quantity ?? 0}
                  showAllBranchesLink
                />
              </React.Fragment>
            )}
          </div>
          <div>
            <Fields.Number
              placeholder="99"
              value={
                data.build_quantity || data.build_quantity === 0
                  ? String(data.build_quantity)
                  : null
              }
              onChange={val => handleChangeQty(Number(val))}
              inputProps={{ min: 1 }}
              error={errors.quantity}
            />
            <span
              css={css`
                text-decoration: underline;
                font-size: 12px;
                display: inline-block;
                color: #666;
              `}
            >
              Order qty: {data.quantity}
            </span>
          </div>
          {useExplicitAllocations && (
            <Grid columns={["auto", "max-content"]} gap={4}>
              <Fields.Number
                placeholder="99"
                value={
                  data.allocated_quantity || data.allocated_quantity === 0
                    ? String(data.allocated_quantity)
                    : null
                }
                onChange={val => onChange({ allocated_quantity: Number(val) })}
                inputProps={{ min: 1 }}
                disabled={!displayAllocationColumn}
                error={errors.allocated_quantity}
              />
              {(data.quantity ?? 0) > (data.product_location?.free_stock ?? 0) && (
                <ToolTip
                  hoverContent={() => (
                    <React.Fragment>
                      <P>More stock than is available has been allocated.</P>
                      <P>
                        Allocated quantity will be adjusted to the max available free stock on save.
                      </P>
                    </React.Fragment>
                  )}
                >
                  <P size="large" blockSpacing={0}>
                    *
                  </P>
                </ToolTip>
              )}
            </Grid>
          )}
          {shouldShowDiscount && <div />}
          {!isCustomerViewing && (
            <div>
              {data.product &&
              (useQuoteOrderStandardPricing
                ? data.standard_price_pennies
                : data.cost_price_pennies) ? (
                <Fields.Price
                  placeholder="0.00"
                  value={
                    useQuoteOrderStandardPricing
                      ? data.standard_price_pennies ?? 0
                      : data.cost_price_pennies
                  }
                  prefix={false}
                  disabled
                  inPennies
                />
              ) : (
                <Fields.Number placeholder="Unavailable" value={null} disabled />
              )}
            </div>
          )}

          {!hidePriceColumn && !quote.isInternalTransfer && <div></div>}

          {hideMarginColumn || quote.isInternalTransfer ? null : data.product && <div />}
        </Grid>
        {type == "quote" && (
          <div
            css={css`
              padding: 8px 0 0 0;
            `}
          >
            <Fields.Checkbox
              label="Optional"
              onChange={() => onChange({ is_rich_quote_optional: !data.is_rich_quote_optional })}
              value={!!data.is_rich_quote_optional}
            />
          </div>
        )}
        {data.product && quote.customer && (
          <React.Fragment>
            <Spacer />
            <ProductInfoDisplayPanel
              productId={data.product.id}
              customerId={quote.customer.id}
              isOpen={focused}
              hideResourceId={quote.id}
              displayCurrency={quote.currency_name}
            />
          </React.Fragment>
        )}
      </div>
      <RemoveRowButton onRemove={onRemove} />
    </React.Fragment>
  )
}
